// @flow

import React, { useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import * as Actions from 'main-app/store/Actions';
import { i18n, getCompanyCurrency, getCurrencySymbol } from 'shared/utils';
import AddInvoiceMutation from 'main-app/mutations/AddInvoice';
import UpdateInvoiceMutation from 'main-app/mutations/UpdateInvoice';
import Divider from 'shared/components/common/Divider';
import AddressAutocomplete from 'main-app/components/AddressAutocomplete';
import Button from 'shared/components/common/Button';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'shared/components/modal';
import {
  Formik,
  Form,
  FieldGroup,
  FieldGroupRow,
  SelectOrderField,
  SelectCustomerField,
} from 'shared/components/form';

const LineItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 8px;
  font-size: 13px;
`;

const LineItemsTh = styled.th`
  text-align: left;
  padding: 6px 8px;
  border-bottom: 2px solid #e0e0e0;
  color: #555;
  font-weight: 600;
`;

const LineItemsTd = styled.td`
  padding: 4px 8px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
`;

const PriceInput = styled.input`
  width: 90px;
  padding: 4px 6px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  text-align: right;
  &:focus {
    outline: none;
    border-color: #4a90e2;
  }
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  color: #e53935;
  cursor: pointer;
  font-size: 16px;
  padding: 0 4px;
  line-height: 1;
  &:hover { opacity: 0.7; }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 4px 0;
  font-size: 13px;
  color: #333;
`;

const SummaryLabel = styled.span`
  margin-right: 16px;
  font-weight: 600;
`;

const SummaryValue = styled.span`
  min-width: 80px;
  text-align: right;
`;

type Props = {
  onClose: () => void,
  onSuccess?: (invoice: Object) => void,
  onReopen?: () => void,
  createAnotherInvoice?: boolean,
  invoice?: ?Object,
  defaultOrder?: ?Object,
};

const INVOICE_STATUS_OPTIONS = [
  { label: 'Draft', value: 'DRAFT' },
  { label: 'Sent', value: 'SENT' },
  { label: 'Partially Paid', value: 'PARTIALLY_PAID' },
  { label: 'Paid', value: 'PAID' },
  { label: 'Overdue', value: 'OVERDUE' },
  { label: 'Void', value: 'VOID' },
];

const AddUpdateInvoiceModal = ({
  onClose,
  onSuccess,
  onReopen,
  createAnotherInvoice,
  invoice,
  defaultOrder,
}: Props) => {
  const isEditing = !!invoice;

  // Seed line items from existing invoice lines (edit mode) or defaultOrder yields
  const seedLineItems = () => {
    if (isEditing && invoice.lineItems) {
      return invoice.lineItems.map((l, i) => ({
        key: i,
        itemId: l.item ? l.item.id : null,
        name: l.description || (l.item && l.item.name) || '',
        quantity: l.quantity,
        unitPrice: l.unitPrice,
        discountPercent: l.discountPercent || 0,
      }));
    }
    if (defaultOrder && defaultOrder.yields) {
      return (defaultOrder.yields.edges || []).map((e, i) => ({
        key: i,
        itemId: e.node.item ? e.node.item.id : null,
        name: e.node.item ? e.node.item.name : '',
        quantity: e.node.orderedQuantity,
        unitPrice: e.node.unitPrice || 0,
        discountPercent: e.node.discountPercent || 0,
      }));
    }
    return [];
  };

  const [lineItems, setLineItems] = useState(seedLineItems);

  const updateLineItem = (key, field, value) => {
    setLineItems(prev =>
      prev.map(li => li.key === key ? { ...li, [field]: value } : li),
    );
  };

  const removeLineItem = key => {
    setLineItems(prev => prev.filter(li => li.key !== key));
  };

  const calcLineTotal = li => {
    const qty = parseFloat(li.quantity) || 0;
    const price = parseFloat(li.unitPrice) || 0;
    const disc = parseFloat(li.discountPercent) || 0;
    return Math.round(qty * price * (1 - disc / 100) * 100) / 100;
  };

  const language = {
    title: isEditing ? i18n.t('Edit Invoice') : i18n.t('New Invoice'),
    submitButton: isEditing ? i18n.t('Save') : i18n.t('Create Invoice'),
  };

  return (
    <Modal isOpen onClose={onClose}>
      <Formik
        initialValues={{
          orderId: invoice && invoice.order
            ? invoice.order.id
            : defaultOrder
              ? { value: defaultOrder.id, label: defaultOrder.orderNumber }
              : '',
          customerId: invoice
            ? invoice.customer.id
            : defaultOrder && defaultOrder.customer
              ? { value: defaultOrder.customer.id, label: defaultOrder.customer.name }
              : '',
          status: invoice ? invoice.status : 'DRAFT',
          currency: invoice ? invoice.currency || getCompanyCurrency() : getCompanyCurrency(),
          discountPercent: invoice ? invoice.discountPercent || 0 : 0,
          taxRate: invoice ? invoice.taxRate || 0 : 0,
          billingAddressLine1: invoice
            ? invoice.billingAddressLine1 || ''
            : (defaultOrder && defaultOrder.customer
              ? defaultOrder.customer.billingAddressLine1
                || (defaultOrder.customer.defaultAddress && defaultOrder.customer.defaultAddress.addressLine1)
                || ''
              : ''),
          billingAddressLine2: invoice
            ? invoice.billingAddressLine2 || ''
            : (defaultOrder && defaultOrder.customer
              ? defaultOrder.customer.billingAddressLine2
                || (defaultOrder.customer.defaultAddress && defaultOrder.customer.defaultAddress.addressLine2)
                || ''
              : ''),
          billingAddressCity: invoice
            ? invoice.billingAddressCity || ''
            : (defaultOrder && defaultOrder.customer
              ? defaultOrder.customer.billingAddressCity
                || (defaultOrder.customer.defaultAddress && defaultOrder.customer.defaultAddress.addressCity)
                || ''
              : ''),
          billingAddressState: invoice
            ? invoice.billingAddressState || ''
            : (defaultOrder && defaultOrder.customer
              ? defaultOrder.customer.billingAddressState
                || (defaultOrder.customer.defaultAddress && defaultOrder.customer.defaultAddress.addressState)
                || ''
              : ''),
          billingAddressZip: invoice
            ? invoice.billingAddressZip || ''
            : (defaultOrder && defaultOrder.customer
              ? defaultOrder.customer.billingAddressZip
                || (defaultOrder.customer.defaultAddress && defaultOrder.customer.defaultAddress.addressZip)
                || ''
              : ''),
          issueDate: invoice ? invoice.issueDate || null : null,
          dueDate: invoice ? invoice.dueDate || null : null,
          notes: invoice ? invoice.notes || '' : '',
          terms: invoice ? invoice.terms || '' : '',
          isPartial: invoice ? (invoice.partialPercent != null && invoice.partialPercent < 100) : false,
          partialPercent: invoice && invoice.partialPercent != null && invoice.partialPercent < 100
            ? String(invoice.partialPercent)
            : '50',
          milestoneLabel: invoice ? invoice.milestoneLabel || '' : '',
        }}
        validationSchema={Yup.object().shape({
          customerId: Yup.mixed().required(i18n.t('Customer is required')),
        })}
        onSubmit={async (values, { setSubmitting }) => {
          const orderIdVal =
            values.orderId && typeof values.orderId === 'object'
              ? values.orderId.value
              : values.orderId;
          const customerIdVal =
            values.customerId && typeof values.customerId === 'object'
              ? values.customerId.value
              : values.customerId;

          const input = {
            orderId: orderIdVal || null,
            customerId: customerIdVal,
            currency: values.currency || getCompanyCurrency(),
            discountPercent: parseFloat(values.discountPercent) || 0,
            taxRate: parseFloat(values.taxRate) || 0,
            billingAddressLine1: values.billingAddressLine1 || null,
            billingAddressLine2: values.billingAddressLine2 || null,
            billingAddressCity: values.billingAddressCity || null,
            billingAddressState: values.billingAddressState || null,
            billingAddressZip: values.billingAddressZip || null,
            issueDate: values.issueDate || null,
            dueDate: values.dueDate || null,
            notes: values.notes || null,
            terms: values.terms || null,
            partialPercent: values.isPartial ? (parseFloat(values.partialPercent) || 100) : 100,
            milestoneLabel: values.isPartial && values.milestoneLabel ? values.milestoneLabel : null,
            lineItems: lineItems.map(li => ({
              itemId: li.itemId || null,
              description: li.name || null,
              quantity: parseFloat(li.quantity) || 1,
              unitPrice: parseFloat(li.unitPrice) || 0,
              discountPercent: parseFloat(li.discountPercent) || 0,
            })),
          };

          if (isEditing) {
            input.id = invoice.id;
            input.status = values.status;
          }

          const mutation = isEditing
            ? UpdateInvoiceMutation
            : AddInvoiceMutation;

          try {
            const response = await mutation.commit({
              variables: { input },
            });

            const newInvoice = isEditing
              ? response.updateInvoice.invoiceEdge.node
              : response.addInvoice.invoiceEdge.node;

            if (values.createAnotherInvoice && onReopen) {
              onReopen();
            } else {
              onClose();
              if (onSuccess) {
                onSuccess(newInvoice);
              }
            }

            Actions.alertNotification(
              isEditing
                ? i18n.t('Invoice updated successfully')
                : i18n.t('Invoice created successfully'),
              'success',
            );
          } catch (e) {
            setSubmitting(false);
            Actions.alertNotification(e.message, 'error');
          }
        }}
        render={({
          errors,
          values,
          isValid,
          isSubmitting,
          handleSubmit,
          setFieldValue,
        }) => (
          <Form>
            <ModalHeader header={language.title} onClose={onClose} />
            <ModalBody withPadding>
              <FieldGroupRow
                left={
                  <SelectCustomerField
                    name="customerId"
                    error={errors.customerId}
                    onChange={customer => {
                      if (!customer || !customer.data) return;
                      const c = customer.data;
                      const da = c.defaultAddress;
                      setFieldValue('billingAddressLine1', c.billingAddressLine1 || (da && da.addressLine1) || '');
                      setFieldValue('billingAddressLine2', c.billingAddressLine2 || (da && da.addressLine2) || '');
                      setFieldValue('billingAddressCity', c.billingAddressCity || (da && da.addressCity) || '');
                      setFieldValue('billingAddressState', c.billingAddressState || (da && da.addressState) || '');
                      setFieldValue('billingAddressZip', c.billingAddressZip || (da && da.addressZip) || '');
                    }}
                  />
                }
                right={
                  <SelectOrderField
                    name="orderId"
                    label={i18n.t('Order (Optional)')}
                    error={errors.orderId}
                    clearable
                    onChange={order => {
                      if (!order || !order.value) {
                        setLineItems([]);
                        return;
                      }
                      const data = order.data;
                      if (data && data.customer) {
                        setFieldValue('customerId', {
                          value: data.customer.id,
                          label: data.customer.name,
                          data: data.customer,
                        });
                        // Auto-populate billing address from customer
                        const c = data.customer;
                        const da = c.defaultAddress;
                        setFieldValue('billingAddressLine1', c.billingAddressLine1 || (da && da.addressLine1) || '');
                        setFieldValue('billingAddressLine2', c.billingAddressLine2 || (da && da.addressLine2) || '');
                        setFieldValue('billingAddressCity', c.billingAddressCity || (da && da.addressCity) || '');
                        setFieldValue('billingAddressState', c.billingAddressState || (da && da.addressState) || '');
                        setFieldValue('billingAddressZip', c.billingAddressZip || (da && da.addressZip) || '');
                      }
                      // Populate line items from order yields
                      if (data && data.yields) {
                        const items = (data.yields.edges || []).map((e, i) => ({
                          key: i,
                          itemId: e.node.item ? e.node.item.id : null,
                          name: e.node.item ? e.node.item.name : '',
                          quantity: e.node.orderedQuantity,
                          unitPrice: e.node.unitPrice || 0,
                          discountPercent: e.node.discountPercent || 0,
                        }));
                        setLineItems(items);
                      }
                    }}
                  />
                }
              />

              {isEditing && (
                <FieldGroupRow
                  left={
                    <FieldGroup
                      name="status"
                      type="select"
                      label={i18n.t('Status')}
                      options={INVOICE_STATUS_OPTIONS}
                      error={errors.status}
                    />
                  }
                  right={
                    <FieldGroup
                      name="currency"
                      label={i18n.t('Currency')}
                      placeholder="USD"
                      error={errors.currency}
                    />
                  }
                />
              )}

              {!isEditing && (
                <FieldGroupRow
                  left={
                    <FieldGroup
                      name="currency"
                      label={i18n.t('Currency')}
                      placeholder="USD"
                      error={errors.currency}
                    />
                  }
                />
              )}

              <Divider style={{ marginTop: 8, marginBottom: 24 }} />

              <FieldGroupRow
                left={
                  <FieldGroup
                    name="discountPercent"
                    label={i18n.t('Discount %')}
                    placeholder="0"
                    error={errors.discountPercent}
                  />
                }
                right={
                  <FieldGroup
                    name="taxRate"
                    label={i18n.t('Tax Rate %')}
                    placeholder="0"
                    error={errors.taxRate}
                  />
                }
              />

              <FieldGroupRow
                left={
                  <FieldGroup
                    name="issueDate"
                    type="date"
                    label={i18n.t('Issue Date')}
                    placeholder={i18n.t('Date')}
                    error={errors.issueDate}
                    allowPastDates
                  />
                }
                right={
                  <FieldGroup
                    name="dueDate"
                    type="date"
                    label={i18n.t('Due Date')}
                    placeholder={i18n.t('Date')}
                    error={errors.dueDate}
                    allowPastDates
                  />
                }
              />

              <div style={{ marginTop: 8, padding: '12px 0', borderTop: '1px solid #f0f0f0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#333' }}>
                  <input
                    type="checkbox"
                    checked={!!values.isPartial}
                    onChange={e => setFieldValue('isPartial', e.target.checked)}
                    style={{ width: 15, height: 15, cursor: 'pointer' }}
                  />
                  {i18n.t('Partial / Milestone Invoice')}
                </label>
                {values.isPartial && (
                  <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                    <div style={{ width: 110 }}>
                      <FieldGroup
                        name="partialPercent"
                        label={i18n.t('% of Total')}
                        placeholder="50"
                        error={errors.partialPercent}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <FieldGroup
                        name="milestoneLabel"
                        label={i18n.t('Milestone Label')}
                        placeholder={i18n.t('e.g. 50% Deposit')}
                        error={errors.milestoneLabel}
                      />
                    </div>
                  </div>
                )}
              </div>

              <Divider style={{ marginTop: 8, marginBottom: 24 }} />

              {/* Line Items */}
              {lineItems.length > 0 && (() => {
                const currency = values.currency || getCompanyCurrency();
                const sym = getCurrencySymbol(currency);
                const discountPct = parseFloat(values.discountPercent) || 0;
                const taxRate = parseFloat(values.taxRate) || 0;
                const subtotal = lineItems.reduce((s, li) => s + calcLineTotal(li), 0);
                const discountAmount = Math.round(subtotal * discountPct / 100 * 100) / 100;
                const taxAmount = Math.round((subtotal - discountAmount) * taxRate / 100 * 100) / 100;
                const total = Math.round((subtotal - discountAmount + taxAmount) * 100) / 100;
                return (
                  <>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, color: '#333' }}>
                      {i18n.t('Line Items')}
                    </div>
                    <LineItemsTable>
                      <thead>
                        <tr>
                          <LineItemsTh>{i18n.t('Item')}</LineItemsTh>
                          <LineItemsTh style={{ textAlign: 'right' }}>{i18n.t('Qty')}</LineItemsTh>
                          <LineItemsTh style={{ textAlign: 'right' }}>{i18n.t('Unit Price')}</LineItemsTh>
                          <LineItemsTh style={{ textAlign: 'right' }}>{i18n.t('Total')}</LineItemsTh>
                          <LineItemsTh />
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map(li => (
                          <tr key={li.key}>
                            <LineItemsTd>{li.name || '—'}</LineItemsTd>
                            <LineItemsTd style={{ textAlign: 'right' }}>{li.quantity}</LineItemsTd>
                            <LineItemsTd style={{ textAlign: 'right' }}>
                              <PriceInput
                                type="number"
                                value={li.unitPrice}
                                min="0"
                                step="0.01"
                                onChange={e => updateLineItem(li.key, 'unitPrice', e.target.value)}
                              />
                            </LineItemsTd>
                            <LineItemsTd style={{ textAlign: 'right' }}>
                              {sym}{calcLineTotal(li).toFixed(2)}
                            </LineItemsTd>
                            <LineItemsTd>
                              <RemoveBtn type="button" onClick={() => removeLineItem(li.key)}>×</RemoveBtn>
                            </LineItemsTd>
                          </tr>
                        ))}
                      </tbody>
                    </LineItemsTable>
                    <SummaryRow>
                      <SummaryLabel>{i18n.t('Subtotal:')}</SummaryLabel>
                      <SummaryValue>{sym}{subtotal.toFixed(2)}</SummaryValue>
                    </SummaryRow>
                    {discountPct > 0 && (
                      <SummaryRow>
                        <SummaryLabel>{i18n.t('Discount ({{pct}}%):', { pct: discountPct })}</SummaryLabel>
                        <SummaryValue>-{sym}{discountAmount.toFixed(2)}</SummaryValue>
                      </SummaryRow>
                    )}
                    {taxRate > 0 && (
                      <SummaryRow>
                        <SummaryLabel>{i18n.t('Tax ({{rate}}%):', { rate: taxRate })}</SummaryLabel>
                        <SummaryValue>{sym}{taxAmount.toFixed(2)}</SummaryValue>
                      </SummaryRow>
                    )}
                    <SummaryRow style={{ fontWeight: 700, fontSize: 14, borderTop: '2px solid #333', paddingTop: 8, marginTop: 4 }}>
                      <SummaryLabel>{i18n.t('Total:')}</SummaryLabel>
                      <SummaryValue>{sym}{total.toFixed(2)}</SummaryValue>
                    </SummaryRow>
                    {values.isPartial && (() => {
                      const pct = parseFloat(values.partialPercent) || 0;
                      const milestoneAmt = Math.round(total * pct / 100 * 100) / 100;
                      const label = values.milestoneLabel || i18n.t('{{pct}}% Milestone', { pct });
                      return (
                        <SummaryRow style={{ color: '#1890ff', fontWeight: 600, marginTop: 4 }}>
                          <SummaryLabel>{label}:</SummaryLabel>
                          <SummaryValue>{sym}{milestoneAmt.toFixed(2)}</SummaryValue>
                        </SummaryRow>
                      );
                    })()}
                    <Divider style={{ marginTop: 16, marginBottom: 24 }} />
                  </>
                );
              })()}

              <AddressAutocomplete
                value={values.billingAddressLine1 || ''}
                label={i18n.t('Billing Address 1')}
                placeholder={i18n.t('Address Line 1')}
                error={errors.billingAddressLine1}
                onChange={val => setFieldValue('billingAddressLine1', val)}
                onAddressSelect={({ line1, city, state, zip }) => {
                  if (line1) setFieldValue('billingAddressLine1', line1);
                  if (city) setFieldValue('billingAddressCity', city);
                  if (state) setFieldValue('billingAddressState', state);
                  if (zip) setFieldValue('billingAddressZip', zip);
                }}
              />
              <FieldGroupRow
                left={
                  <FieldGroup
                    name="billingAddressLine2"
                    label={i18n.t('Billing Address 2')}
                    placeholder={i18n.t('Address Line 2')}
                    error={errors.billingAddressLine2}
                  />
                }
                right={
                  <FieldGroup
                    name="billingAddressCity"
                    label={i18n.t('City')}
                    placeholder={i18n.t('City')}
                    error={errors.billingAddressCity}
                  />
                }
              />
              <FieldGroupRow
                left={
                  <FieldGroup
                    name="billingAddressState"
                    label={i18n.t('State')}
                    placeholder={i18n.t('State')}
                    error={errors.billingAddressState}
                  />
                }
                right={
                  <FieldGroup
                    name="billingAddressZip"
                    label={i18n.t('Postal Code')}
                    placeholder={i18n.t('Zip Code')}
                    error={errors.billingAddressZip}
                  />
                }
              />

              <Divider style={{ marginTop: 8, marginBottom: 24 }} />

              <FieldGroup
                name="notes"
                type="textarea"
                label={i18n.t('Notes')}
                placeholder={i18n.t('Enter notes here...')}
                error={errors.notes}
              />
              <FieldGroup
                name="terms"
                type="textarea"
                label={i18n.t('Payment Terms')}
                placeholder={i18n.t('e.g. Net 30, Due on receipt')}
                error={errors.terms}
              />

              {!isEditing && (
                <FieldGroup
                  label={i18n.t('Create Another Invoice')}
                  sideLabel="right"
                  name="createAnotherInvoice"
                  type="toggle"
                />
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                type="submit"
                theme="blue"
                disabled={!isValid}
                loading={isSubmitting}
                onClick={handleSubmit}
              >
                {language.submitButton}
              </Button>
            </ModalFooter>
          </Form>
        )}
      />
    </Modal>
  );
};

AddUpdateInvoiceModal.defaultProps = {
  onSuccess: undefined,
  onReopen: undefined,
  createAnotherInvoice: false,
  invoice: null,
  defaultOrder: null,
};

export default AddUpdateInvoiceModal;
